import {
  alpha,
  Avatar,
  Box,
  Drawer,
  IconButton,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { memo, useEffect } from 'react'
import { IoChevronBack, IoChevronForward } from 'react-icons/io5'
import Scrollbar from 'react-perfect-scrollbar'
import { Link } from 'react-router-dom'

import { Logo, Support } from '@/components'
import { useTheme as useContextTheme } from '@/contexts'
import { Alert, LoginUser, User } from '@/types'
import { capitalizeSentence as c } from '@/utils'

import SidebarItems from './SidebarItems'

const SIDEBAR_WIDTH = 280
const SIDEBAR_COLLAPSED_WIDTH = 80

function Sidebar(props: SidebarProps) {
  const { onMobileClose, open, user, token, collapsed, onToggleCollapse, alerts } = props

  const isMobile = useMediaQuery(({ breakpoints }: Theme) => breakpoints.down('lg'))
  const theme = useTheme()
  const { isDark } = useContextTheme()

  useEffect(() => {
    if (open && !isMobile) {
      onMobileClose()
    }
  }, [open, isMobile, onMobileClose])

  const firstName = c(user.name).split(' ')[0]
  const lastName = c(user.name).split(' ').slice(-1)[0]
  const sidebarWidth = collapsed && !isMobile ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH

  return (
    <>
      <Drawer
        anchor="left"
        onClose={onMobileClose}
        open={open || !isMobile}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'background.paper',
              width: sidebarWidth,
              paddingTop: '4rem',
              borderRight: 1,
              borderColor: 'divider',
              boxShadow: theme.shadows[2],
              transition: theme.transitions.create(['width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          },
        }}
        variant={isMobile ? 'temporary' : 'persistent'}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}>
          <Scrollbar options={{ suppressScrollX: true }}>
            <Box
              sx={{
                display: { xs: 'flex', lg: 'none' },
                justifyContent: 'center',
                p: 3,
                borderBottom: 1,
                borderColor: 'divider',
              }}>
              <Link to="/">
                <Logo colored={!isDark} />
              </Link>
            </Box>

            <Box sx={{ p: collapsed && !isMobile ? 1 : 2 }}>
              <Tooltip title="Ver perfil" placement="bottom" disableInteractive>
                <Link to="/perfil" style={{ textDecoration: 'none' }}>
                  <Box
                    sx={{
                      p: collapsed && !isMobile ? 1 : 2.5,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      border: 1,
                      borderColor: alpha(theme.palette.primary.main, 0.12),
                      borderRadius: 3,
                      transition: theme.transitions.create(['all'], {
                        duration: theme.transitions.duration.short,
                      }),
                      '&:hover': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}>
                    <Stack
                      direction={collapsed && !isMobile ? 'column' : 'row'}
                      alignItems="center"
                      spacing={collapsed && !isMobile ? 1 : 2}>
                      <Avatar
                        src={user.photo || ''}
                        sx={{
                          cursor: 'pointer',
                          height: collapsed && !isMobile ? 40 : 48,
                          width: collapsed && !isMobile ? 40 : 48,
                          border: 2,
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                          transition: theme.transitions.create(['all'], {
                            duration: theme.transitions.duration.short,
                          }),
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: theme.shadows[6],
                          },
                        }}
                      />

                      {(!collapsed || isMobile) && (
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                              color: 'text.primary',
                              fontSize: '0.95rem',
                              lineHeight: 1.2,
                            }}>
                            {firstName} {lastName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                            }}>
                            {user.type}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Link>
              </Tooltip>
            </Box>

            <Box sx={{ px: collapsed && !isMobile ? 1 : 2, pb: 2 }}>
              <SidebarItems
                token={token}
                alerts={alerts}
                collapsed={collapsed && !isMobile}
              />
            </Box>
          </Scrollbar>

          <Support collapsed={collapsed && !isMobile} />
        </Box>
      </Drawer>

      {!isMobile && (open || !isMobile) && (
        <Box
          sx={{
            position: 'fixed',
            left: sidebarWidth - 14,
            top: collapsed ? 86 : 108,
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create(['left'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}>
          <Tooltip
            title={collapsed ? 'Expandir barra lateral' : 'Recolher barra lateral'}
            placement="right">
            <IconButton
              onClick={onToggleCollapse}
              sx={{
                width: 30,
                height: 30,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: theme.shadows[6],
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'scale(1.1)',
                  boxShadow: theme.shadows[12],
                },
                transition: theme.transitions.create(['all'], {
                  duration: theme.transitions.duration.short,
                }),
              }}>
              {collapsed ? <IoChevronForward size={16} /> : <IoChevronBack size={16} />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </>
  )
}

type SidebarProps = {
  onMobileClose: () => void
  open: boolean
  user: LoginUser | User
  token: string
  collapsed: boolean
  onToggleCollapse: () => void
  alerts: Alert[]
}

export default memo(Sidebar)
